import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Search, Plus, Save, Trash2, Link2, Sparkles, Loader2, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useUserStore } from '@/store/userStore';

const API = process.env.REACT_APP_BACKEND_URL;

const TAB_STYLE = (active) => ({
  padding: '4px 12px',
  borderRadius: 6,
  fontSize: 11,
  fontWeight: active ? 600 : 400,
  background: active ? 'rgba(74,158,255,0.15)' : 'transparent',
  border: `1px solid ${active ? 'rgba(74,158,255,0.4)' : 'transparent'}`,
  color: active ? '#4A9EFF' : 'var(--vz-text-muted)',
  cursor: 'pointer',
  transition: 'all .15s',
});

export function SedeOverridesTab() {
  const qc = useQueryClient();
  const { token } = useUserStore();
  const authHeader = () => ({ Authorization: `Bearer ${token}` });

  const [tab, setTab] = useState('mapeo'); // 'mapeo' | 'auditoria'
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ text_norm: '', sede_codigo: '', sede_label: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // ── Reapply overrides state ───────────────────────────────────────────────
  const [reapplying, setReapplying] = useState(false);
  const [reapplyResult, setReapplyResult] = useState(null);

  const reapplyAllOverrides = async () => {
    setReapplying(true);
    setReapplyResult(null);
    try {
      const r = await axios.post(`${API}/api/admin/reapply-all-overrides`, {}, { headers: authHeader() });
      setReapplyResult(r.data);
      toast.success(`Re-aplicación completada: ${r.data.total_updated} registros actualizados`);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al re-aplicar overrides');
    } finally { setReapplying(false); }
  };
  const [auditRunning, setAuditRunning] = useState(false);
  const [auditResults, setAuditResults] = useState(null);
  const [dismissed, setDismissed] = useState(new Set());
  const [approved, setApproved] = useState(new Set());
  const [approvingItem, setApprovingItem] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sede-text-overrides'],
    queryFn: () => axios.get(`${API}/api/admin/sede-text-overrides`, { headers: authHeader() }).then(r => r.data),
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const filtered = items.filter(i =>
    !search ||
    i.text_norm?.toLowerCase().includes(search.toLowerCase()) ||
    i.sede_codigo?.toLowerCase().includes(search.toLowerCase()) ||
    i.sede_label?.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paged = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const handleSave = async () => {
    if (!form.text_norm.trim() || !form.sede_codigo.trim()) {
      toast.error('Texto y código son obligatorios'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/sede-text-overrides`, form, { headers: authHeader() });
      toast.success('Mapeo guardado');
      setForm({ text_norm: '', sede_codigo: '', sede_label: '' });
      setShowForm(false);
      qc.invalidateQueries({ queryKey: ['sede-text-overrides'] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (text_norm) => {
    if (!window.confirm(`¿Eliminar mapeo "${text_norm}"?`)) return;
    setDeleting(text_norm);
    try {
      await axios.delete(`${API}/api/admin/sede-text-overrides/${encodeURIComponent(text_norm)}`, { headers: authHeader() });
      toast.success('Mapeo eliminado');
      qc.invalidateQueries({ queryKey: ['sede-text-overrides'] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al eliminar');
    } finally { setDeleting(null); }
  };

  const handleEdit = (item) => {
    setForm({ text_norm: item.text_norm, sede_codigo: item.sede_codigo, sede_label: item.sede_label || '' });
    setShowForm(true);
  };

  // ── Auditoría IA handlers ─────────────────────────────────────────────────
  const runAudit = async () => {
    setAuditRunning(true);
    setAuditResults(null);
    setDismissed(new Set());
    setApproved(new Set());
    try {
      const r = await axios.post(`${API}/api/admin/sede-ai-audit`, {}, { headers: authHeader(), timeout: 180000 });
      setAuditResults(r.data);
      toast.success(`Auditoría completada: ${r.data.suggestions_count} sugerencias`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al ejecutar la auditoría IA');
    } finally { setAuditRunning(false); }
  };

  const approveItem = async (item) => {
    setApprovingItem(item.wrong);
    try {
      // Save as sede_text_override mapping: wrong text → canonical label
      const payload = {
        text_norm: item.wrong.toLowerCase(),
        sede_codigo: '',   // admin will set this manually — or we try to resolve it
        sede_label: item.canonical,
        source: 'ai_audit',
      };
      // Try to resolve sede_codigo from catalog
      const catRes = await axios.get(`${API}/api/admin/catalog-health`, { headers: authHeader() });
      const centros = catRes.data?.centros || [];
      const match = centros.find(c =>
        c.nombre?.toLowerCase().trim() === item.canonical.toLowerCase().trim()
      );
      if (match?.codigo) payload.sede_codigo = match.codigo;

      if (!payload.sede_codigo) {
        toast.warning(`No se encontró código para "${item.canonical}". Añade el mapeo manualmente con el código correcto.`);
        setApprovingItem(null);
        return;
      }

      await axios.post(`${API}/api/admin/sede-text-overrides`, payload, { headers: authHeader() });
      setApproved(prev => new Set([...prev, item.wrong]));
      toast.success(`Mapeo guardado: "${item.wrong}" → ${payload.sede_codigo}`);
      qc.invalidateQueries({ queryKey: ['sede-text-overrides'] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al aprobar');
    } finally { setApprovingItem(null); }
  };

  const dismissItem = (wrong) => setDismissed(prev => new Set([...prev, wrong]));

  return (
    <div className="space-y-5" data-testid="sede-overrides-tab">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-montserrat font-bold text-sm text-white mb-0.5">Mapeo Sedes Texto → Código</h3>
          <p className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>
            Catálogo de equivalencias texto-libre → código de sede. Sirve para enriquecer registros que ArcGIS envía con nombre en lugar de código numérico.
          </p>
        </div>
        <Badge style={{ background: 'rgba(74,158,255,0.12)', color: '#4A9EFF', border: '1px solid rgba(74,158,255,0.3)' }}>
          {isLoading ? '...' : `${total} mapeos`}
        </Badge>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2">
        <button style={TAB_STYLE(tab === 'mapeo')} onClick={() => setTab('mapeo')}>
          <span className="flex items-center gap-1.5"><Link2 size={11} /> Mapeos ({total})</span>
        </button>
        <button style={TAB_STYLE(tab === 'auditoria')} onClick={() => setTab('auditoria')} data-testid="sede-tab-auditoria">
          <span className="flex items-center gap-1.5"><Sparkles size={11} /> Auditoría IA</span>
        </button>
      </div>

      {/* ── MAPEO TAB ─────────────────────────────────────────────────────── */}
      {tab === 'mapeo' && (
        <div className="space-y-4">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1 max-w-md">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--vz-text-muted)' }} />
              <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Buscar por texto, código o nombre..."
                className="h-8 text-xs pl-8" data-testid="sede-overrides-search" />
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
              onClick={() => { setForm({ text_norm: '', sede_codigo: '', sede_label: '' }); setShowForm(!showForm); }}
              data-testid="sede-overrides-add-btn">
              <Plus size={12} /> Añadir mapeo
            </Button>
            <Button size="sm" className="h-8 text-xs gap-1.5" disabled={reapplying}
              style={{ background: 'rgba(255,193,7,0.12)', border: '1px solid rgba(255,193,7,0.3)', color: '#FFC107' }}
              onClick={reapplyAllOverrides}
              data-testid="btn-reapply-overrides">
              {reapplying ? <><Loader2 size={11} className="animate-spin" /> Aplicando...</> : <><RefreshCw size={11} /> Re-aplicar a BD</>}
            </Button>
          </div>

          {reapplyResult && (
            <div className="p-3 rounded-lg text-xs space-y-1.5" style={{ background: 'rgba(255,193,7,0.06)', border: '1px solid rgba(255,193,7,0.2)' }} data-testid="reapply-result">
              <p className="font-semibold" style={{ color: '#FFC107' }}>
                Re-aplicación completada — {reapplyResult.total_updated} registros actualizados
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                {Object.entries(reapplyResult.steps || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between px-2 py-1 rounded" style={{ background: 'rgba(0,0,0,0.2)' }}>
                    <span style={{ color: 'var(--vz-text-muted)' }}>{k.replace(/_/g, ' ')}</span>
                    <span className="font-mono font-bold" style={{ color: v?.error ? '#FF5252' : '#4CAF50' }}>
                      {v?.error ? 'ERROR' : `+${v?.updated ?? 0}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showForm && (
            <div className="p-4 rounded-xl space-y-3" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)' }}>
              <p className="text-xs font-semibold" style={{ color: 'var(--vz-text-primary)' }}>Nuevo mapeo / editar existente</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wide block mb-1" style={{ color: 'var(--vz-text-muted)' }}>Texto (como llega de ArcGIS)</label>
                  <Input value={form.text_norm} onChange={(e) => setForm({ ...form, text_norm: e.target.value })}
                    placeholder="ej: ute torrejon" className="h-8 text-xs" data-testid="sede-overrides-text-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide block mb-1" style={{ color: 'var(--vz-text-muted)' }}>Sede código</label>
                  <Input value={form.sede_codigo} onChange={(e) => setForm({ ...form, sede_codigo: e.target.value })}
                    placeholder="ej: 5000110" className="h-8 text-xs font-mono" data-testid="sede-overrides-code-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide block mb-1" style={{ color: 'var(--vz-text-muted)' }}>Sede etiqueta (opcional)</label>
                  <Input value={form.sede_label} onChange={(e) => setForm({ ...form, sede_label: e.target.value })}
                    placeholder="ej: L y R TORREJON CALZADA" className="h-8 text-xs" data-testid="sede-overrides-label-input" />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowForm(false)}>Cancelar</Button>
                <Button size="sm" className="h-7 text-xs gap-1.5" disabled={saving} onClick={handleSave} data-testid="sede-overrides-save-btn">
                  <Save size={11} /> {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </div>
          )}

          {isLoading ? <Skeleton className="h-60 w-full" /> : filtered.length === 0 ? (
            <p className="text-xs py-8 text-center" style={{ color: 'var(--vz-text-muted)' }}>
              {search ? 'Sin resultados para tu búsqueda.' : 'No hay mapeos definidos.'}
            </p>
          ) : (
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--vz-border-subtle)' }}>
              <Table>
                <TableHeader>
                  <TableRow style={{ borderColor: 'var(--vz-border-subtle)', background: 'var(--vz-bg-elevated)' }}>
                    <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Texto (entrada)</TableHead>
                    <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Sede código</TableHead>
                    <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Sede etiqueta</TableHead>
                    <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Origen</TableHead>
                    <TableHead className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paged.map((item, i) => (
                    <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                      <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-primary)' }}>{item.text_norm || <span style={{ color: '#FF5252' }}>— (texto vacío)</span>}</TableCell>
                      <TableCell className="text-xs py-2 font-mono" style={{ color: '#4A9EFF', fontWeight: 600 }}>{item.sede_codigo || <span style={{ color: '#FF5252' }}>— (código vacío)</span>}</TableCell>
                      <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-secondary)' }}>{item.sede_label || '—'}</TableCell>
                      <TableCell className="text-xs py-2">
                        <Badge style={{ background: item.source === 'admin_ui' ? 'rgba(76,175,80,0.15)' : 'rgba(155,155,155,0.12)', color: item.source === 'admin_ui' ? '#4CAF50' : 'var(--vz-text-muted)' }}>
                          {item.source === 'admin_ui' ? 'UI' : 'Excel/Config'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs py-2 flex gap-1.5">
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => handleEdit(item)}
                          data-testid={`sede-overrides-edit-${item.text_norm}`}>
                          <Link2 size={10} /> Editar
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" style={{ color: '#F44336' }}
                          disabled={deleting === item.text_norm} onClick={() => handleDelete(item.text_norm)}
                          data-testid={`sede-overrides-delete-${item.text_norm}`}>
                          <Trash2 size={10} /> {deleting === item.text_norm ? '...' : 'Eliminar'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-3 py-2 text-[11px]" style={{ color: 'var(--vz-text-muted)' }}>
                  <span>Página {pageSafe} de {totalPages} · {filtered.length} mapeos{search ? ' (filtrados)' : ''}</span>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" disabled={pageSafe <= 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="sede-overrides-prev-page">Anterior</Button>
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" disabled={pageSafe >= totalPages}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="sede-overrides-next-page">Siguiente</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── AUDITORÍA IA TAB ──────────────────────────────────────────────── */}
      {tab === 'auditoria' && (
        <div className="space-y-4" data-testid="sede-auditoria-panel">
          <div className="p-4 rounded-xl border space-y-3" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'rgba(74,158,255,0.2)' }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-white flex items-center gap-2">
                  <Sparkles size={14} style={{ color: '#4A9EFF' }} /> Auditoría de centros con IA
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--vz-text-muted)' }}>
                  Claude analiza todos los nombres de centros únicos en la BD, detecta grafías erróneas y sugiere el nombre canónico correcto. Las aprobadas se añaden como mapeo al instante.
                </p>
              </div>
              <Button size="sm" className="h-8 text-xs gap-1.5 shrink-0"
                style={{ background: 'rgba(74,158,255,0.15)', border: '1px solid rgba(74,158,255,0.4)', color: '#4A9EFF' }}
                onClick={runAudit} disabled={auditRunning} data-testid="btn-sede-run-audit">
                {auditRunning ? <><Loader2 size={12} className="animate-spin" /> Analizando...</> : <><Sparkles size={12} /> Iniciar auditoría</>}
              </Button>
            </div>
            {auditResults && (
              <div className="flex gap-4 text-xs pt-1 border-t" style={{ borderColor: 'var(--vz-border-subtle)', color: 'var(--vz-text-muted)' }}>
                <span><strong style={{ color: '#4A9EFF' }}>{auditResults.total_labels}</strong> centros únicos</span>
                <span><strong style={{ color: '#4A9EFF' }}>{auditResults.candidate_groups}</strong> grupos candidatos</span>
                <span><strong style={{ color: '#FFD600' }}>{auditResults.suggestions_count}</strong> sugerencias</span>
                <span><strong style={{ color: '#4CAF50' }}>{approved.size}</strong> aprobadas</span>
                <span><strong style={{ color: 'var(--vz-text-muted)' }}>{dismissed.size}</strong> ignoradas</span>
              </div>
            )}
          </div>

          {auditRunning && (
            <div className="flex flex-col items-center justify-center py-16 gap-3" style={{ color: 'var(--vz-text-muted)' }}>
              <Loader2 size={28} className="animate-spin" style={{ color: '#4A9EFF' }} />
              <p className="text-sm">Claude está analizando los centros... (puede tardar 20-40 segundos)</p>
            </div>
          )}

          {!auditRunning && auditResults && (() => {
            const pending = auditResults.suggestions.filter(s => !approved.has(s.wrong) && !dismissed.has(s.wrong));
            return (
              <div className="space-y-3">
                {pending.length === 0 && (
                  <p className="text-xs py-8 text-center" style={{ color: approved.size > 0 ? '#4CAF50' : 'var(--vz-text-muted)' }}>
                    {approved.size > 0 ? 'Todas las sugerencias procesadas.' : 'No se encontraron typos nuevos en los centros.'}
                  </p>
                )}
                {pending.length > 0 && (
                  <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--vz-border-subtle)' }}>
                    <Table>
                      <TableHeader>
                        <TableRow style={{ borderColor: 'var(--vz-border-subtle)', background: 'var(--vz-bg-elevated)' }}>
                          <TableHead className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--vz-text-muted)' }}>Centro incorrecto (ArcGIS)</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--vz-text-muted)' }}>Nombre canónico sugerido</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wide hidden sm:table-cell" style={{ color: 'var(--vz-text-muted)' }}>Motivo</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--vz-text-muted)' }}>Acción</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pending.map((item, i) => (
                          <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                            <TableCell className="text-xs py-2 font-mono" style={{ color: '#FF7043' }}>{item.wrong}</TableCell>
                            <TableCell className="text-xs py-2 font-medium" style={{ color: '#4CAF50' }}>{item.canonical}</TableCell>
                            <TableCell className="text-xs py-2 hidden sm:table-cell" style={{ color: 'var(--vz-text-muted)', maxWidth: 180 }}>{item.reason}</TableCell>
                            <TableCell className="text-xs py-2">
                              <div className="flex gap-1">
                                <Button size="sm" className="h-6 px-2 text-[10px] gap-1"
                                  style={{ background: 'rgba(76,175,80,0.15)', border: '1px solid rgba(76,175,80,0.3)', color: '#4CAF50' }}
                                  onClick={() => approveItem(item)} disabled={approvingItem === item.wrong}
                                  data-testid={`sede-approve-${i}`}>
                                  {approvingItem === item.wrong ? <Loader2 size={9} className="animate-spin" /> : <CheckCircle2 size={10} />} Aprobar
                                </Button>
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] gap-1"
                                  style={{ color: 'var(--vz-text-muted)' }} onClick={() => dismissItem(item.wrong)}
                                  data-testid={`sede-dismiss-${i}`}>
                                  <XCircle size={10} /> Ignorar
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })()}

          {!auditRunning && !auditResults && (
            <div className="flex flex-col items-center justify-center py-16 gap-2" style={{ color: 'var(--vz-text-muted)' }}>
              <Sparkles size={28} style={{ color: 'rgba(74,158,255,0.4)' }} />
              <p className="text-sm">Pulsa "Iniciar auditoría" para que Claude analice los nombres de centros en la BD</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
